/**
 * heroTyping.js
 *
 * This script manages a typing animation effect for the titles on the homepage's hero section.
 * It observes changes to the active slide in a Swiper carousel and triggers typing animations
 * when a new slide becomes active, resetting the text when the slide deactivates.
 */

export default function heroTyping() {
	// Check if the body has the 'home' class
	if (!document.body.classList.contains("home")) return;
	const heroSection = document.querySelector(".heroSection.-hasTitleAnimation");
	if (!heroSection) return;

	const swiperContainer = document.querySelector(".swiper-container");
	if (!swiperContainer) return;

	// Get dynamic typing delay from Swiper config
	function getTypingDelay(container) {
		if (!container) return 500;
		const swiperAttr = container.getAttribute("swiper");
		if (!swiperAttr) return 500;

		try {
			const config = JSON.parse(swiperAttr.replace(/&quot;/g, '"').replace(/&amp;/g, "&"));
			const swiperDelay = config?.autoplay?.delay;
			if (typeof swiperDelay === "number") {
				return Math.max(100, swiperDelay - 500); // buffer before next slide
			}
		} catch (err) {
			console.warn("Failed to parse Swiper delay from swiper attribute:", err);
		}
		return 500;
	}

	// Throttle the MutationObserver callback to prevent performance issues
	let mutationTimeout;
	const observer = new MutationObserver((mutations) => {
		if (mutationTimeout) return;

		mutationTimeout = setTimeout(() => {
			mutations.forEach((mutation) => {
				if (mutation.type === "attributes" && mutation.attributeName === "class" && mutation.target.classList.contains("swiper-slide")) {
					const h1 = mutation.target.querySelector("h1.heroSection__title");

					if (mutation.target.classList.contains("swiper-slide-active")) {
						if (h1) {
							const delay = getTypingDelay(swiperContainer);
							startTypingOnce(h1, delay);
						}
					} else {
						if (h1) resetText(h1);
					}
				}
			});
			mutationTimeout = null;
		}, 100);
	});

	observer.observe(swiperContainer, {
		attributes: true,
		subtree: true,
		attributeFilter: ["class"],
	});

	// Trigger typing effect for the initial active slide
	const initialActiveSlide = swiperContainer.querySelector(".swiper-slide-active");
	if (initialActiveSlide) {
		const h1 = initialActiveSlide.querySelector("h1.heroSection__title");
		if (h1) {
			const delay = getTypingDelay(swiperContainer);
			startTypingOnce(h1, delay);
		}
	}

	// Add event listener for pagination
	const paginationContainer = swiperContainer.querySelector(".swiper-pagination");
	if (paginationContainer) {
		paginationContainer.addEventListener("click", (event) => {
			if (event.target.classList.contains("swiper-pagination-bullet")) {
				setTimeout(() => {
					const activeSlide = swiperContainer.querySelector(".swiper-slide-active");
					if (activeSlide) {
						const h1 = activeSlide.querySelector("h1.heroSection__title");
						if (h1) {
							resetText(h1);
							const delay = getTypingDelay(swiperContainer);
							startTypingOnce(h1, delay);
						}
					}
				}, 100);
			}
		});
	}

	// Initialize typing with accessibility
	function startTypingOnce(element, delay = 1000) {
		if (element.hasAttribute("data-typed")) return;

		if (!element.dataset.fullHtml) {
			// Use textContent instead of innerHTML to prevent garbage tags
			element.dataset.fullHtml = element.textContent.trim();
		}
		element.setAttribute("aria-live", "polite");
		element.setAttribute("aria-hidden", "false");
		element.style.visibility = "visible";
		element.innerHTML = "";
		setTimeout(() => typeText(element), delay);
		element.setAttribute("data-typed", "true");
	}

	// Reset text on inactive slides
	function resetText(element) {
		if (element.typingTimer) {
			clearTimeout(element.typingTimer);
			delete element.typingTimer;
		}
		if (element.dataset.fullHtml) {
			element.innerHTML = element.dataset.fullHtml;
		}
		element.setAttribute("aria-hidden", "true");
		element.style.visibility = "hidden";
		element.removeAttribute("data-typed");
	}

	function measureText(text, element) {
		const measureSpan = document.createElement("span");
		measureSpan.style.visibility = "hidden";
		measureSpan.style.position = "absolute";
		measureSpan.style.whiteSpace = "pre";
		const styles = window.getComputedStyle(element);
		measureSpan.style.font = styles.font;
		measureSpan.style.letterSpacing = styles.letterSpacing;
		measureSpan.style.textTransform = styles.textTransform;
		measureSpan.style.padding = styles.padding;
		measureSpan.innerHTML = text;
		document.body.appendChild(measureSpan);
		const width = measureSpan.offsetWidth;
		document.body.removeChild(measureSpan);
		return width;
	}

	function shouldWrapWord(currentLine, word, element, nextWord) {
		const containerWidth = element.offsetWidth;
		const tolerancePixels = 5;

		const lineWithWord = currentLine + word;
		const lineWidth = measureText(lineWithWord, element);

		if (lineWidth > containerWidth + tolerancePixels) {
			return true;
		}

		if (nextWord && !nextWord.isTag) {
			const lineWithBothWords = lineWithWord + nextWord.text;
			const widthWithBothWords = measureText(lineWithBothWords, element);

			if (widthWithBothWords <= containerWidth + tolerancePixels) {
				return false;
			}

			if (word.length <= 3) {
				return widthWithBothWords > containerWidth + tolerancePixels * 2;
			}
		}

		return lineWidth > containerWidth;
	}

	function getWordBoundaries(text) {
		const words = [];
		let currentWord = "";
		let currentWordStart = 0;

		for (let i = 0; i < text.length; i++) {
			const char = text[i];
			if (char === "<") {
				let tagEnd = text.indexOf(">", i);
				if (tagEnd === -1) tagEnd = text.length;
				if (currentWord) {
					words.push({
						text: currentWord,
						start: currentWordStart,
						end: i,
					});
					currentWord = "";
				}
				words.push({
					text: text.substring(i, tagEnd + 1),
					start: i,
					end: tagEnd + 1,
					isTag: true,
				});
				i = tagEnd;
			} else if (/\s/.test(char)) {
				if (currentWord) {
					words.push({
						text: currentWord,
						start: currentWordStart,
						end: i,
					});
					currentWord = "";
				}
				words.push({
					text: char,
					start: i,
					end: i + 1,
					isSpace: true,
				});
			} else {
				if (!currentWord) {
					currentWordStart = i;
				}
				currentWord += char;
			}
		}

		if (currentWord) {
			words.push({
				text: currentWord,
				start: currentWordStart,
				end: text.length,
			});
		}

		return words;
	}

	function typeText(element) {
		if (element.typingTimer) {
			clearTimeout(element.typingTimer);
			delete element.typingTimer;
		}

		const parentElement = element.closest(".heroSection__titleSlideDown");
		const fullText = element.dataset.fullHtml;
		element.innerHTML = "";

		if (parentElement) parentElement.classList.add("initial-cursor");

		const wrapper = document.createElement("div");
		wrapper.style.position = "relative";
		element.appendChild(wrapper);

		let currentLine = document.createElement("div");
		wrapper.appendChild(currentLine);

		const cursorSpan = document.createElement("span");
		cursorSpan.classList.add("active-span");
		currentLine.appendChild(cursorSpan);

		const words = getWordBoundaries(fullText);
		let wordIndex = 0;
		let charIndex = 0;
		const typingSpeed = 50;

		function updateCursorPosition(span) {
			if (span && span.nextSibling !== cursorSpan) {
				span.parentNode.insertBefore(cursorSpan, span.nextSibling);
			}
		}

		function typeNextCharacter() {
			if (wordIndex >= words.length) {
				const lastSpan = currentLine.lastElementChild;
				updateCursorPosition(lastSpan);
				delete element.typingTimer;
				return;
			}

			const currentWord = words[wordIndex];
			const nextWord = words[wordIndex + 1];

			if (currentWord.isTag || charIndex === 0) {
				if (!currentWord.isTag && !currentWord.isSpace) {
					if (shouldWrapWord(currentLine.textContent, currentWord.text, element, nextWord)) {
						currentLine = document.createElement("div");
						wrapper.appendChild(currentLine);
						currentLine.appendChild(cursorSpan);
					}
				}
			}

			if (currentWord.isTag) {
				currentLine.innerHTML += currentWord.text;
				wordIndex++;
				charIndex = 0;
				typeNextCharacter();
				return;
			}

			const char = currentWord.text[charIndex];
			const span = document.createElement("span");
			span.textContent = char;
			currentLine.insertBefore(span, cursorSpan);

			updateCursorPosition(span);

			charIndex++;

			if (charIndex >= currentWord.text.length) {
				wordIndex++;
				charIndex = 0;
			}

			element.typingTimer = setTimeout(typeNextCharacter, typingSpeed);
		}

		setTimeout(() => {
			if (parentElement) parentElement.classList.remove("initial-cursor");
			typeNextCharacter();
		}, 0);
	}
}
